import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { 
  MessageSquare, 
  User, 
  Mail, 
  Phone, 
  Building, 
  Calendar, 
  DollarSign, 
  Clock,
  Eye,
  CheckCircle,
  XCircle,
  PhoneCall
} from 'lucide-react';
import axios from 'axios';
import { API_URLS, API_CONFIG } from '../../../config/api';

const AdminQuoteRequests = () => {
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchQuoteRequests();
  }, []);

  const fetchQuoteRequests = async () => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/backend/api.php?action=getQuoteRequests`);
      setQuoteRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API_CONFIG.BASE_URL}/backend/api.php?action=updateQuoteStatus&quote_id=${quoteId}&status=${newStatus}`);
      fetchQuoteRequests();
    } catch (error) {
      console.error('Error updating quote status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500', text: 'Beklemede' },
      reviewed: { color: 'bg-blue-500', text: 'İncelendi' },
      contacted: { color: 'bg-green-500', text: 'İletişim Kuruldu' },
      completed: { color: 'bg-purple-500', text: 'Tamamlandı' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.color} text-white`}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatProjectId = (projectId) => {
    if (!projectId) return '';
    // Kebab-case veya snake_case'i Title Case'e çevir
    return projectId
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatAdditionalRequirements = (requirements) => {
    if (!requirements) return '';
    
    try {
      // JSON formatında mı kontrol et
      const parsed = JSON.parse(requirements);
      
      // SEO ve Reklam paketleri için özel format
      if (parsed.source === 'seo_services' || parsed.source === 'advertising_services') {
        const name = parsed.package_name || 'Paket';
        return `${name} fiyat talebi`;
      }
      
      // Eğer project_id içeriyorsa
      if (parsed.project_id) {
        const formattedProjectId = formatProjectId(parsed.project_id);
        // Diğer alanları da kontrol et
        const otherFields = Object.keys(parsed)
          .filter(key => key !== 'project_id')
          .map(key => {
            const value = parsed[key];
            if (typeof value === 'string') {
              return `${key}: ${value}`;
            }
            return `${key}: ${JSON.stringify(value)}`;
          });
        
        let result = `Proje Tipi: ${formattedProjectId}`;
        if (otherFields.length > 0) {
          result += '\n' + otherFields.join('\n');
        }
        return result;
      }
      
      // JSON ama project_id yoksa, tüm alanları göster
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // JSON değilse, direkt string olarak döndür
      // Ama eğer sadece project_id gibi görünüyorsa formatla
      const trimmed = requirements.trim();
      if (trimmed.match(/^[a-z0-9-]+$/i)) {
        return formatProjectId(trimmed);
      }
      return requirements;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Teklif talepleri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6 overflow-x-hidden max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Talepler</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
            Müşterilerden gelen teklif taleplerini yönetin
          </p>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-4">
          <div className="text-center sm:text-right">
            <p className="text-xl sm:text-2xl font-bold text-blue-600">{quoteRequests.length}</p>
            <p className="text-xs sm:text-sm text-gray-600">Toplam Talep</p>
          </div>
          <div className="text-center sm:text-right">
            <p className="text-xl sm:text-2xl font-bold text-yellow-600">
              {quoteRequests.filter(q => q.status === 'pending').length}
            </p>
            <p className="text-xs sm:text-sm text-gray-600">Bekleyen</p>
          </div>
        </div>
      </div>

      {quoteRequests.length === 0 ? (
        <Card>
          <CardContent className="p-4 sm:p-8 text-center">
            <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-xl font-semibold text-gray-900 mb-2">Henüz teklif talebi yok</h3>
            <p className="text-xs sm:text-sm text-gray-600">Müşterilerden teklif talepleri geldiğinde burada görünecek.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:gap-6 w-full">
          {quoteRequests.map((quote) => (
            <Card key={quote.id} className="hover:shadow-md transition-shadow overflow-hidden w-full max-w-full">
              <CardContent className="p-3 sm:p-4 lg:p-6 overflow-x-hidden max-w-full">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 w-full">
                  <div className="flex-1 min-w-0 max-w-full overflow-hidden w-full">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 truncate">
                        {quote.service_title}
                      </h3>
                      {(() => {
                        try {
                          const ar = quote.additional_requirements && JSON.parse(quote.additional_requirements);
                          if (ar && ar.source === 'project_question') return <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Proje talebi</Badge>;
                        } catch (_) {}
                        return null;
                      })()}
                      {getStatusBadge(quote.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="flex items-center space-x-2 min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-gray-600 truncate">{quote.customer_name}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 min-w-0 max-w-full">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-gray-600 break-all">{quote.customer_email}</span>
                      </div>
                      
                      {quote.customer_phone && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-600 truncate">{quote.customer_phone}</span>
                        </div>
                      )}
                      
                      {quote.company_name && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Building className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-600 truncate">{quote.company_name}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      {quote.budget_range && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-600 truncate">{quote.budget_range}</span>
                        </div>
                      )}
                      
                      {quote.timeline && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-600 truncate">{quote.timeline}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2 min-w-0">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-gray-600 truncate">{formatDate(quote.created_at)}</span>
                      </div>
                    </div>
                    
                    <p className="text-xs sm:text-sm text-gray-700 line-clamp-2">
                      {quote.project_description}
                    </p>
                  </div>
                  
                  <div className="flex flex-row sm:flex-col gap-2 sm:space-y-2 sm:ml-4 flex-shrink-0 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedQuote(quote);
                        setShowDetails(true);
                      }}
                      className="flex-1 sm:flex-none text-xs sm:text-sm min-w-0"
                    >
                      <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                      <span className="truncate">Detaylar</span>
                    </Button>
                    
                    <Select 
                      value={quote.status} 
                      onValueChange={(value) => updateQuoteStatus(quote.id, value)}
                    >
                      <SelectTrigger className="w-full sm:w-32 text-xs sm:text-sm min-w-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Beklemede</SelectItem>
                        <SelectItem value="reviewed">İncelendi</SelectItem>
                        <SelectItem value="contacted">İletişim Kuruldu</SelectItem>
                        <SelectItem value="completed">Tamamlandı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quote Details Modal */}
      {showDetails && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <Card className="w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <CardHeader className="p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <CardTitle className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900 truncate">
                  Teklif Detayları - {selectedQuote.service_title}
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowDetails(false)}
                  className="self-end sm:self-auto"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4 sm:space-y-6 p-3 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Müşteri Bilgileri</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 min-w-0">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm truncate">{selectedQuote.customer_name}</span>
                    </div>
                    <div className="flex items-center space-x-2 min-w-0 max-w-full">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm break-all">{selectedQuote.customer_email}</span>
                    </div>
                    {selectedQuote.customer_phone && (
                      <div className="flex items-center space-x-2 min-w-0">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">{selectedQuote.customer_phone}</span>
                      </div>
                    )}
                    {selectedQuote.company_name && (
                      <div className="flex items-center space-x-2 min-w-0">
                        <Building className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">{selectedQuote.company_name}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Proje Bilgileri</h4>
                  <div className="space-y-2">
                    {selectedQuote.budget_range && (
                      <div className="flex items-center space-x-2 min-w-0">
                        <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">{selectedQuote.budget_range}</span>
                      </div>
                    )}
                    {selectedQuote.timeline && (
                      <div className="flex items-center space-x-2 min-w-0">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">{selectedQuote.timeline}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 min-w-0">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm truncate">{formatDate(selectedQuote.created_at)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(selectedQuote.status)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Proje Açıklaması</h4>
                <p className="text-xs sm:text-sm text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  {selectedQuote.project_description}
                </p>
              </div>
              
              {selectedQuote.additional_requirements && (
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Ek Gereksinimler</h4>
                  <p className="text-xs sm:text-sm text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg whitespace-pre-line">
                    {formatAdditionalRequirements(selectedQuote.additional_requirements)}
                  </p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 pt-3 sm:pt-4 border-t">
                <Button 
                  className="bg-green-600 hover:bg-green-700 w-full sm:w-auto text-xs sm:text-sm"
                  onClick={() => {
                    updateQuoteStatus(selectedQuote.id, 'contacted');
                    setShowDetails(false);
                  }}
                >
                  <PhoneCall className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  İletişim Kuruldu
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowDetails(false)}
                  className="w-full sm:w-auto text-xs sm:text-sm"
                >
                  Kapat
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminQuoteRequests;

